import { useApolloClient } from "@apollo/client/react"
import { useFormikContext } from "formik"
import { DateTime } from "luxon"
import { useMemo, useState, useEffect, useCallback } from "react"

import { IsVenueOpenDocument } from "@/graphql/graphql"
import i18n from "@/i18n"
import { type ReservationFormValues } from "@/pages/public/venue"
import { createHoursList } from "@/shared/datetime"
import { formatTableName } from "@/shared/tables"
import { showErrorToast } from "@/shared/toast"
import { getWeekdayHours, type OpeningHour } from "@/utils/opening_hours"

const getRoundedBookingThreshold = (value: DateTime) => {
  const totalMinutes = value.minute + value.second / 60 + value.millisecond / 60000
  const roundedMinutes = Math.ceil(totalMinutes / 15) * 15

  return value.startOf("hour").plus({ minutes: roundedMinutes }).set({ second: 0, millisecond: 0 })
}

export interface Table {
  id: string
  name: string
  minCapacity?: number | null
  maxCapacity?: number | null
  tableGroup: {
    id: string
    name: string
  }
}

export interface VenuePlan {
  id: string
  sectionName?: string | null
  plan?: string | null
  position: number
  default: boolean
}

export interface ReservationDetailsStepProps {
  tables: Table[]
  openingHours: OpeningHour[]
  timezone: string
  country: string
  venueSlug: string
  venueName: string
  venueLogoUrl?: string | null
  venuePlans: VenuePlan[]
  address?: string | null
  city?: string | null
  minAdvanceBookingTime?: number | null
  minReservationTime?: number | null
  maxReservationTime?: number | null
  mobileSheet?: boolean
  editMode?: boolean
  originalReservation?: {
    id: string
    startTime: string
    endTime: string
    peopleAmount: number
    tableId: string
  } | null
  onBack: () => void
  onClose?: () => void
  onNext: () => void
  onError: (message: string) => void
  onShowHowItWorks?: () => void
}

type UseReservationDetailsStateProps = Pick<
  ReservationDetailsStepProps,
  | "tables"
  | "openingHours"
  | "timezone"
  | "venueSlug"
  | "minAdvanceBookingTime"
  | "venuePlans"
  | "editMode"
  | "originalReservation"
  | "onNext"
  | "onError"
>

type TimingState = {
  startHour: number
  endHour: number
  timeOptions: [string, DateTime][]
  scheduleTimeOptions: [string, DateTime][]
  closedUntil: DateTime | null
  endTimeOptions: [string, DateTime][]
}

type DerivedState = {
  isValid: boolean
  isSchedulingUnchanged: boolean
  selectedTableName: string | null
  availableTablesCount: number
  durationDisplay: string | null
  selectedTimeRange: string | null
}

const getOpeningRange = (date: DateTime | null, openingHours: OpeningHour[]) => {
  if (!date) return { startHour: 8, endHour: 23 }

  const weekday = date.weekday % 7
  const hours = getWeekdayHours(openingHours, weekday, date)

  if (!hours || hours.closed || !hours.startTime || !hours.endTime) {
    return { startHour: 8, endHour: 23 }
  }

  const [startHour] = hours.startTime.split(":").map(Number)
  const [endHour] = hours.endTime.split(":").map(Number)

  return { startHour, endHour }
}

const useReservationTimingState = ({
  values,
  now,
  openingHours,
  minAdvanceBookingTime,
  editMode,
  originalReservation,
}: {
  values: Pick<ReservationFormValues, "date" | "startTime">
  now: DateTime
  openingHours: OpeningHour[]
  minAdvanceBookingTime?: number | null
  editMode: boolean
  originalReservation?: ReservationDetailsStepProps["originalReservation"]
}): TimingState => {
  const { startHour, endHour } = useMemo(() => getOpeningRange(values.date, openingHours), [values.date, openingHours])

  const earliestBookingTime = useMemo(() => {
    if (minAdvanceBookingTime) {
      return now.plus({ seconds: minAdvanceBookingTime })
    }
    return now
  }, [now, minAdvanceBookingTime])

  const roundedBookingThreshold = useMemo(() => getRoundedBookingThreshold(earliestBookingTime), [earliestBookingTime])

  const originalStartTime = useMemo(
    () => (editMode && originalReservation?.startTime ? DateTime.fromISO(originalReservation.startTime) : null),
    [editMode, originalReservation?.startTime],
  )

  const canKeepOriginalStartTime = useMemo(() => {
    if (!values.date || !originalStartTime) return false

    return (
      values.date.hasSame(originalStartTime, "day") &&
      originalStartTime.hour >= startHour &&
      originalStartTime.hour <= endHour &&
      originalStartTime < roundedBookingThreshold
    )
  }, [values.date, originalStartTime, startHour, endHour, roundedBookingThreshold])

  const shouldAllowTime = useMemo(
    () => (dt: DateTime) =>
      dt > earliestBookingTime || (canKeepOriginalStartTime && originalStartTime?.toMillis() === dt.toMillis()),
    [earliestBookingTime, canKeepOriginalStartTime, originalStartTime],
  )

  const timeOptions = useMemo(() => {
    if (!values.date) return []
    return createHoursList(values.date, startHour, endHour).filter(([_, dt]) => shouldAllowTime(dt))
  }, [values.date, startHour, endHour, shouldAllowTime])

  const scheduleTimeOptions = useMemo(() => {
    if (!values.date) return []
    return createHoursList(values.date, startHour, endHour)
  }, [values.date, startHour, endHour])

  const closedUntil = useMemo(() => {
    if (!values.date || !values.date.hasSame(now, "day")) return null

    if (canKeepOriginalStartTime && originalStartTime) {
      return originalStartTime
    }

    return roundedBookingThreshold
  }, [values.date, now, canKeepOriginalStartTime, originalStartTime, roundedBookingThreshold])

  const endTimeOptions = useMemo(() => {
    if (!values.date || !values.startTime) return []
    const startDt = DateTime.fromISO(values.startTime)
    return createHoursList(values.date, startHour, endHour).filter(([_, dt]) => shouldAllowTime(dt) && dt > startDt)
  }, [values.date, values.startTime, startHour, endHour, shouldAllowTime])

  return {
    startHour,
    endHour,
    timeOptions,
    scheduleTimeOptions,
    closedUntil,
    endTimeOptions,
  }
}

const useReservationDerivedState = ({
  values,
  tables,
  hasConflicts,
  editMode,
  originalReservation,
  getTableCapacityStatus,
}: {
  values: Pick<ReservationFormValues, "date" | "startTime" | "endTime" | "peopleAmount" | "tableId">
  tables: Table[]
  hasConflicts: boolean
  editMode: boolean
  originalReservation?: ReservationDetailsStepProps["originalReservation"]
  getTableCapacityStatus: (table: Table) => { disabled: boolean; tooltip: string | null }
}): DerivedState => {
  const isValid = !!(
    values.date &&
    values.startTime &&
    values.endTime &&
    values.peopleAmount > 0 &&
    values.tableId &&
    !hasConflicts
  )

  const isSchedulingUnchanged = useMemo(() => {
    if (!editMode || !originalReservation) return false
    return (
      values.tableId === originalReservation.tableId &&
      values.startTime === originalReservation.startTime &&
      values.endTime === originalReservation.endTime &&
      values.peopleAmount === originalReservation.peopleAmount
    )
  }, [editMode, originalReservation, values.tableId, values.startTime, values.endTime, values.peopleAmount])

  const selectedTableName = useMemo(() => {
    if (!values.tableId) return null
    const table = tables.find((t) => t.id === values.tableId)
    return table ? formatTableName(table) : null
  }, [values.tableId, tables])

  const availableTablesCount = useMemo(
    () => tables.filter((table) => !getTableCapacityStatus(table).disabled).length,
    [tables, getTableCapacityStatus],
  )

  const durationDisplay = useMemo(() => {
    if (!values.startTime || !values.endTime) return null
    const start = DateTime.fromISO(values.startTime)
    const end = DateTime.fromISO(values.endTime)
    const diffInMinutes = end.diff(start, "minutes").minutes

    if (diffInMinutes < 60) {
      return i18n.t("app.date.duration.minutes", { count: diffInMinutes })
    }

    const hours = Math.floor(diffInMinutes / 60)
    const minutes = diffInMinutes % 60

    if (minutes === 0) {
      return i18n.t("app.date.duration.hours", { count: hours })
    }

    return `${hours}h ${minutes}min`
  }, [values.startTime, values.endTime])

  const selectedTimeRange = useMemo(() => {
    if (!values.startTime || !values.endTime) return null
    const start = DateTime.fromISO(values.startTime)
    const end = DateTime.fromISO(values.endTime)
    return `${start.toFormat("HH:mm")} - ${end.toFormat("HH:mm")}`
  }, [values.startTime, values.endTime])

  return {
    isValid,
    isSchedulingUnchanged,
    selectedTableName,
    availableTablesCount,
    durationDisplay,
    selectedTimeRange,
  }
}

export const useReservationDetailsState = ({
  tables,
  openingHours,
  timezone,
  venueSlug,
  minAdvanceBookingTime,
  venuePlans,
  editMode = false,
  originalReservation,
  onNext,
  onError,
}: UseReservationDetailsStateProps) => {
  const { values, setFieldValue } = useFormikContext<ReservationFormValues>()
  const now = DateTime.now().setZone(timezone)
  const [isChecking, setIsChecking] = useState(false)
  const [hasConflicts, setHasConflicts] = useState(false)
  const [isLegendTooltipOpen, setIsLegendTooltipOpen] = useState(false)
  const apolloClient = useApolloClient()

  const getTableCapacityStatus = useCallback(
    (table: Table) => {
      const { peopleAmount } = values
      if (table.minCapacity && peopleAmount < table.minCapacity) {
        return {
          disabled: true,
          tooltip: i18n.t("app.settings.venues.venue_layout.table_capacity.min_required", {
            count: table.minCapacity,
          }),
        }
      }
      if (table.maxCapacity && peopleAmount > table.maxCapacity) {
        return {
          disabled: true,
          tooltip: i18n.t("app.settings.venues.venue_layout.table_capacity.max_exceeded", {
            count: table.maxCapacity,
          }),
        }
      }
      return { disabled: false, tooltip: null }
    },
    [values],
  )

  useEffect(() => {
    if (values.tableId) {
      const selectedTable = tables.find((t) => t.id === values.tableId)
      if (selectedTable) {
        const status = getTableCapacityStatus(selectedTable)
        if (status.disabled) {
          void setFieldValue("tableId", "")
        }
      }
    }
  }, [getTableCapacityStatus, setFieldValue, tables, values.tableId])

  const { timeOptions, scheduleTimeOptions, closedUntil, endTimeOptions } = useReservationTimingState({
    values,
    now,
    openingHours,
    minAdvanceBookingTime,
    editMode,
    originalReservation,
  })

  const {
    isValid,
    isSchedulingUnchanged,
    selectedTableName,
    availableTablesCount,
    durationDisplay,
    selectedTimeRange,
  } = useReservationDerivedState({
    values,
    tables,
    hasConflicts,
    editMode,
    originalReservation,
    getTableCapacityStatus,
  })

  const handleNext = async () => {
    if (!isValid) return

    if (isSchedulingUnchanged) {
      onNext()
      return
    }

    setIsChecking(true)

    try {
      const result = await apolloClient.query({
        query: IsVenueOpenDocument,
        variables: {
          slug: venueSlug,
          startTime: values.startTime,
          endTime: values.endTime,
        },
        fetchPolicy: "network-only",
      })

      const isOpen = result.data?.public?.venue?.open

      if (isOpen === false) {
        onError(i18n.t("app.public.new_reservation.errors.venue_closed"))
        setIsChecking(false)
        return
      }

      onNext()
    } catch (error) {
      showErrorToast()
      console.error("Error checking venue availability:", error)
      onError(i18n.t("app.public.new_reservation.errors.generic"))
    } finally {
      setIsChecking(false)
    }
  }

  const hasPlanLayout = venuePlans.length > 0

  return {
    values,
    setFieldValue,
    isChecking,
    hasConflicts,
    setHasConflicts,
    isLegendTooltipOpen,
    setIsLegendTooltipOpen,
    getTableCapacityStatus,
    timeOptions,
    scheduleTimeOptions,
    closedUntil,
    endTimeOptions,
    isValid,
    handleNext,
    selectedTableName,
    availableTablesCount,
    durationDisplay,
    hasPlanLayout,
    selectedTimeRange,
  }
}
